import React, { useEffect, useState } from "react";
import { Button, Progress } from "reactstrap";
import { headerColor } from "../../constants/conv_common";
// import firebase from "../../contexts/firebase";
import { AnalysisError, classifyAnalysisError, classifyEmptyAnalysisResponse } from "../../helpers/analysisErrors";
import { ifxApiClient } from "../../helpers/api";
import { detectBrowserAndOS } from "../../helpers/detectBrowser";
import { EMBED_STAGE } from "../../views/Pages/EmbedScene/common";
import { extractAbilities, getAbilityStatusText, getScoreText } from "./ScoringAndFeedback";  

// this is an older version of the analysis screen, and is only used by the 
// embedded INTERACT SCENE version of the app (i.e., the non-authenticated user version). 
// It is not used in the mobile app or the main web app, nor is it used in the
// INTERACT STORY version of the app.

const AnalysisCard = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      padding: "10px",
      boxShadow: "0px 0px 5px 4px rgba(0,0,0,0.1)",
      borderRadius: "10px",
      margin: "20px 0",
    }}
  >
    {children}
  </div>
);
 

const Column = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      style={{
        width: "350px",
        overflow: "hidden",
        padding: "10px",
        minWidth: "200px",
        margin: "0 10px",
      }}
    >
      {children}
    </div>
  );
};

interface AnalysisScreenProps {
  videoUrl?: string;
  storyId: string;
  sceneId: string;
  userId: string;
  onStageChange: (stage: EMBED_STAGE | null) => void;
  onAnalysisResponse?: (analysisResponse: any) => void;
  analysisResponse?: any;
  isEmbedded?: boolean;
  interactUserId: string;
}

const AnalysisScreen = ({
  videoUrl,
  onStageChange,
  sceneId,
  storyId,
  userId,
  interactUserId,
  onAnalysisResponse,
  analysisResponse: upstreamAnalysisResponse,
  isEmbedded = false,
}: AnalysisScreenProps) => {
  const [analysisResponse, setAnalysisResponse] = useState<{ response?: any; error?: AnalysisError }>({
    response: upstreamAnalysisResponse || null,
    error: undefined,
  });

  console.log("IFX: Entered AnalysisScreen:", analysisResponse);

  useEffect(() => {
    if (analysisResponse.response || analysisResponse.error) {
      return;
    }
    const browserAndOS = detectBrowserAndOS();
    // console.log("********************************************");
    // console.log("IFX:  New AnalysisScreen about to post content to analysis/analyzeembed ");
    // console.log("St, Sc, uid, ", storyId, sceneId, userId, interactUserId);
    // console.log("vid, br, os", videoUrl, browserAndOS.browser, browserAndOS.os);
    // console.log("IFX:  AnalysisScreen detected browser and OS as ", browserAndOS);
    // ifxApiClient.post(`/content/${sceneId}/analyze/v2?method=upload`, {
    //   contentId: sceneId,
    //   contentGuid: sceneId,
    //   user: userId,
    //   uri: videoUrl,
    //   storyid: storyId,
    //   webBrowser: browserAndOS.browser,
    //   os: browserAndOS.os,
    // })
    //  analysisUID is the user id to be used in the ifx_analysis record;
    const analysisUID = (interactUserId === "") ? userId : interactUserId;
    const utterancePayload = {
      uid: analysisUID,
      storyid: storyId,
      sceneid: sceneId,
      uri: videoUrl,
      localfilename: null,
      localaudiofilename: null,
      method: "web",
      webBrowser: browserAndOS.browser,
      os: browserAndOS.os,
      };

    // console.log("utterancePayload:", utterancePayload);
    // console.info("current user : ", firebase.auth.currentUser?.uid); // DEBUG
    // const thisusertoken =  await firebase.auth.currentUser?.getIdToken(); // DEBUG
    // console.info("current user token: ", thisusertoken); // DEBUG

  ifxApiClient.post("/analysis/analyzeembed", utterancePayload)
      .then((response: { data: any }) => {
        if (!response?.data.analyses) {
          setAnalysisResponse({ error: classifyEmptyAnalysisResponse(response?.data), response: undefined });
        } else {
          setAnalysisResponse({ response: response.data, error: undefined });
          onAnalysisResponse?.(response.data);
        }
      })
      .catch(err => {
        const analysisError = classifyAnalysisError(err);
        console.error(`Analysis failed (${analysisError.type}):`, analysisError.details);
        setAnalysisResponse({ error: analysisError, response: undefined });
      });
  }, [analysisResponse, interactUserId, onAnalysisResponse, sceneId, storyId, userId, videoUrl]);

  let content = null;

  if (analysisResponse.error) {
    content = (
      <div
        style={{
          marginTop: "100px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <Column>
          <h5
            style={{
              textAlign: "center",
            }}
          >
            {analysisResponse.error.message}
          </h5>

          <div>
            {analysisResponse.error.canResubmit && (
              <Button
                style={{ marginTop: "100px", color: "white" }}
                block
                color={"info"}
                // clearing the error re-runs the analysis effect with the same recording
                onClick={() => setAnalysisResponse({ response: null, error: undefined })}
              >
                Try again
              </Button>
            )}
            <Button
              style={{ marginBottom: "10px", marginTop: analysisResponse.error.canResubmit ? "10px" : "100px", color: "white" }}
              block
              color={"info"}
              onClick={() => {
                onAnalysisResponse?.(null);
                onStageChange(EMBED_STAGE.RESPONSE_VIDEO_RECORDING);
              }}
            >
              Record again
            </Button>
          </div>
        </Column>
      </div>
    );
  } else if (!analysisResponse.response) {
    content = (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <h4>Analyzing…</h4>
      </div>
    );
  } else {
    const parsedAbilities = extractAbilities(analysisResponse.response);
    const score = analysisResponse?.response?.skills?.current.score || 0;

    content = (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <Column>
          <h3 style={{ color: headerColor }}>Skill Progress</h3>
          <AnalysisCard>
            <h4 style={{ color: headerColor }}>{analysisResponse?.response?.skills?.name}</h4>

            <h2 style={{ color: headerColor, textAlign: "center" }}>
              {Math.trunc(analysisResponse?.response?.skills?.current.score * 100)}
            </h2>
            <Progress value={score * 100} />

            <div style={{ marginTop: "20px" }}>{getScoreText(analysisResponse?.response?.passed, score)}</div>
          </AnalysisCard>

          <div>
            <Button
              // TODO: these styles are shared, could be extracted to a constant.
              style={{ marginBottom: "10px", marginTop: "10px", color: "white" }}
              block
              color={"info"}
              onClick={() => {
                onStageChange(EMBED_STAGE.EXPERT_VIDEO);
              }}
            >
              See expert video
            </Button>

            <Button
              style={{ marginBottom: "10px", marginTop: "10px", color: "white" }}
              block
              color={"info"}
              onClick={() => {
                onAnalysisResponse?.(null);
                onStageChange(EMBED_STAGE.INTERACTION_VIDEO);
              }}
            >
              Try again
            </Button>

            <Button
              // TODO: these styles are shared, could be extracted to a constant.
              style={{ marginBottom: "10px", marginTop: "10px", color: "white" }}
              block
              color={"info"}
              onClick={() => {
                onStageChange(null);
              }}
            >
              {isEmbedded ? "Quit" : "To Quit, please close the active tab"}
            </Button>
          </div>
        </Column>

        <Column>
          <h3 style={{ color: headerColor }}>Suggestions</h3>
          {parsedAbilities.map(({ name, description, points, feedback, numRequired, priority, possiblePoints, abilityPassed }) => (
            <AnalysisCard>
              <h4 style={{ color: headerColor }}>{name}</h4>
              <p style={{ color: "gray", marginLeft: "20px" }}>
              {getAbilityStatusText(name, priority, feedback.length, numRequired, abilityPassed)}
              </p>
              {/* <p>{description}</p> */}

              <div style={{ margin: "10px 0" }}>
                {feedback.map(({ feedback, isPositive }) => {
                  const icon = isPositive ? "icon-plus" : "icon-minus";
                  const color = isPositive ? "text-success" : "text-danger";
                  return (
                    <span className={color}>
                      {" "}
                      <i className={icon} /> {feedback}
                      <br />
                    </span>
                  );
                })}
              </div>

              <p style={{ textAlign: "right" }} className={points ? "text-success" : "text-danger"}>
                {Math.round(points)} points (out of {possiblePoints})
              </p>
            </AnalysisCard>
          ))}
        </Column>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        maxWidth: "100%",
      }}
    >
      {content}
    </div>
  );
};

export default AnalysisScreen;
